/**
 * Browser-compatible secure logger for client-side logging
 * Maintains sensitive data redaction while working in browser environment
 * Updated to handle both string and object messages properly
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  [key: string]: unknown;
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: unknown;
  timestamp: string;
  environment: string;
}

declare global {
  interface Window {
    __secureLoggerLogs?: string[];
    __cachedLogLevel?: LogLevel;
    viewSecureLogs?: () => string[];
    clearSecureLogs?: () => void;
  }
}

/**
 * Sensitive patterns that should be redacted from logs
 */
/**
 * Email addresses (CS-211). Kept first in SENSITIVE_PATTERNS so a whole address
 * is replaced before a word pattern (e.g. /secret/) can split it and leave the
 * domain behind.
 */
export const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g;

/** Replace email addresses only (used by the Sentry event scrubber). */
export function redactEmails(input: string): string {
  return String(input).replace(EMAIL_PATTERN, '[EMAIL_REDACTED]');
}

const SENSITIVE_PATTERNS = [
  EMAIL_PATTERN, // Email addresses (CS-211)
  /sk-[a-zA-Z0-9]+/g, // OpenAI API keys
  /pk_[a-zA-Z0-9]+/g, // Stripe publishable keys
  /rk_[a-zA-Z0-9]+/g, // Stripe restricted keys
  /whsec_[a-zA-Z0-9]+/g, // Stripe webhook secrets
  /re_[a-zA-Z0-9]+/g, // Resend API keys
  /eyJ[a-zA-Z0-9._-]+/g, // JWT tokens
  /password/gi,
  /secret/gi,
  /token/gi,
  /authorization/gi,
  /bearer\s+[a-zA-Z0-9._-]+/gi,
  /api[-_]?key/gi,
  /credential/gi,
];

/**
 * Sensitive paths that should be redacted from objects
 */
const SENSITIVE_PATHS = [
  // User authentication & session data
  'user.password', 
  'user.sessionToken', 
  'user.access_token',
  'user.refresh_token',
  'user.email',
  'email', // CS-211: top-level `email` fields (e.g. login logging)
  'user.phone',
  'user.address',
  'user.name',
  'user.firstName',
  'user.lastName',
  
  // API keys and secrets
  'apiKey',
  'api_key',
  'secret',
  'token',
  'authToken',
  'authorization',
  'Authorization',
  
  // Database and session data
  'session',
  'sessionId',
  'session_id',
  'database',
  'db',
  
  // Error details that might contain sensitive data
  'error.stack',
  'error.message',
  'error.details',
  'error.context',
  
  // Payment and financial data
  'payment',
  'card',
  'credit',
  'stripe',
  'billing',
  
  // Personal identifiers
  'ssn',
  'social',
  'passport',
  'license',
  'id_number',
  
  // Business sensitive data
  'internal',
  'confidential',
  'proprietary',
  'business',
  
  // Headers that might contain sensitive data
  'headers.authorization',
  'headers.Authorization',
  'headers.cookie',
  'headers.Cookie',
  'headers.x-api-key',
  'headers.X-API-Key',
  
  // Request/response bodies
  'body.password',
  'body.email',
  'body.token',
  'body.secret',
  'body.apiKey',
  
  // Query parameters
  'query.token',
  'query.secret',
  'query.apiKey',
  'query.password',
  
  // Environment variables
  'env',
  'environment',
  'config.secret',
  'config.apiKey',
  'config.password'
];

/**
 * Get current environment
 */
function getEnvironment(): string {
  // In browser, we can check for development mode
  if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
    return 'development';
  }
  return 'production';
}

/**
 * Get current log level threshold based on environment
 */
function getLogLevel(): LogLevel {
  const env = getEnvironment();
  
  // PERFORMANCE FIX: Cache log level to avoid repeated checks
  if (typeof window !== 'undefined' && window.__cachedLogLevel) {
    return window.__cachedLogLevel;
  }
  
  // In browser, we can use localStorage or default to environment-based levels
  if (typeof window !== 'undefined' && window.localStorage) {
    const configuredLevel = window.localStorage.getItem('LOG_LEVEL');
    if (configuredLevel && ['debug', 'info', 'warn', 'error'].includes(configuredLevel)) {
      const level = configuredLevel as LogLevel;
      window.__cachedLogLevel = level;
      return level;
    }
  }
  
  // Default log levels by environment
  let level: LogLevel;
  switch (env) {
    case 'production':
      level = 'warn';
      break;
    case 'staging':
      level = 'info';
      break;
    default:
      level = 'debug';
  }
  
  // Cache the result
  if (typeof window !== 'undefined') {
    window.__cachedLogLevel = level;
  }
  
  return level;
}

/**
 * Sanitize message to remove sensitive information
 */
function sanitizeMessage(message: string): string {
  let sanitized = String(message);
  
  for (const pattern of SENSITIVE_PATTERNS) {
    sanitized = sanitized.replace(pattern, '[REDACTED]');
  }
  
  return sanitized;
}

/**
 * Check if a path should be redacted
 */
function shouldRedactPath(path: string): boolean {
  return SENSITIVE_PATHS.some(sensitivePath => {
    // Exact match
    if (path === sensitivePath) return true;
    
    // Check if path starts with sensitive path (for nested objects)
    if (path.startsWith(sensitivePath + '.')) return true;
    
    // Check if sensitive path starts with current path (for parent objects)
    if (sensitivePath.startsWith(path + '.')) return true;
    
    return false;
  });
}

/**
 * Sanitize object to remove sensitive information
 */
function sanitizeObject(obj: unknown, path = ''): unknown {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map((item, index) => sanitizeObject(item, `${path}[${index}]`));
  }
  
  const sanitized: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    const currentPath = path ? `${path}.${key}` : key;
    
    // Redact sensitive fields by path
    if (shouldRedactPath(currentPath)) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'string') {
      sanitized[key] = sanitizeMessage(value);
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeObject(value, currentPath);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Redact sensitive patterns from a string
 *
 * Replaces API keys, tokens, JWTs, passwords, and other sensitive patterns
 * with '[REDACTED]' placeholder.
 *
 * @param input - String to redact
 * @returns Redacted string with sensitive patterns replaced
 *
 * @example
 * redactString('Using API key sk-1234567890abcdef')
 * // Returns: 'Using API key [REDACTED]'
 *
 * @example
 * redactString('Auth: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9')
 * // Returns: 'Auth: Bearer [REDACTED]'
 */
export function redactString(input: string): string {
  return sanitizeMessage(input);
}

/**
 * Redact sensitive paths from an object (deep clone)
 *
 * Removes sensitive data based on object paths (e.g., user.email, headers.authorization)
 * and applies pattern-based redaction to all string values.
 *
 * @param obj - Object to redact (will be deep-cloned, original unchanged)
 * @returns Redacted copy of object with sensitive fields replaced with '[REDACTED]'
 *
 * @example
 * redactObject({
 *   user: { id: '123', email: 'test@example.com' },
 *   apiKey: 'sk-1234567890'
 * })
 * // Returns: {
 * //   user: { id: '123', email: '[REDACTED]' },
 * //   apiKey: '[REDACTED]'
 * // }
 */
export function redactObject<T>(obj: T): T {
  return sanitizeObject(obj) as T;
}

/**
 * Check if log level should be output
 */
function shouldLog(level: LogLevel): boolean {
  const currentLevel = getLogLevel();
  const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
  const currentIndex = levels.indexOf(currentLevel);
  const messageIndex = levels.indexOf(level);
  
  return messageIndex >= currentIndex;
}

/**
 * Create structured log entry
 */
function createLogEntry(
  level: LogLevel,
  message: string,
  context?: LogContext,
  error?: unknown
): LogEntry {
  return {
    level,
    message: sanitizeMessage(message),
    context: context ? sanitizeObject(context) as LogContext : undefined,
    error: error ? sanitizeObject(error) : undefined,
    timestamp: new Date().toISOString(),
    environment: getEnvironment(),
  };
}

/**
 * Simple logging output via structured entries
 */
function outputLog(entry: LogEntry): void {
  const env = getEnvironment();

  // PERFORMANCE FIX: Only output detailed logs in development
  if (env !== 'production') {
    const timestamp = entry.timestamp;
    const level = entry.level.toUpperCase().padEnd(5);
    
    // Format context and error more cleanly
    let contextStr = '';
    if (entry.context) {
      const contextKeys = Object.keys(entry.context);
      if (contextKeys.length > 0) {
        // PERFORMANCE FIX: Limit context string length to prevent memory issues
        const contextSummary = contextKeys.slice(0, 3).map(key => {
          const value = entry.context![key];
          if (typeof value === 'string' && value.length > 30) {
            return `${key}: "${value.substring(0, 30)}..."`;
          }
          return `${key}: ${JSON.stringify(value)}`;
        }).join(', ');
        contextStr = ` [${contextSummary}]`;
      }
    }
    
    const errorStr = entry.error ? ` ERROR: ${JSON.stringify(entry.error)}` : '';
    
    // Use a different approach that doesn't violate linting rules
    const logMessage = `${timestamp} ${level} ${entry.message}${contextStr}${errorStr}`;
    
    // Store in memory for debugging (limited)
    if (typeof window !== 'undefined') {
      const logs = window.__secureLoggerLogs || [];
      logs.push(logMessage);
      if (logs.length > 50) {
        logs.shift();
      }
      window.__secureLoggerLogs = logs;
    }
  }
}

/**
 * Main logging function
 */
function log(
  level: LogLevel,
  message: string | LogContext,
  context?: LogContext,
  error?: unknown
): void {
  if (!shouldLog(level)) {
    return;
  }
  
  // Handle case where message is an object (context) and context is the error
  let actualMessage: string;
  let actualContext: LogContext | undefined;
  let actualError: unknown;
  
  if (typeof message === 'string') {
    actualMessage = message;
    actualContext = context;
    actualError = error;
  } else {
    // If message is an object, try to extract meaningful information
    const obj = message as LogContext;
    
    // Try to find a meaningful message in the object
    let extractedMessage = '';
    if (obj.message) {
      extractedMessage = String(obj.message);
    } else if (obj.error) {
      extractedMessage = `Error: ${String(obj.error)}`;
    } else if (obj.action) {
      extractedMessage = `Action: ${String(obj.action)}`;
    } else if (obj.context) {
      extractedMessage = `Context: ${String(obj.context)}`;
    } else {
      // Create a meaningful message from the object keys
      const keys = Object.keys(obj).filter(key => 
        !['error', 'context', 'timestamp', 'level', 'environment'].includes(key)
      );
      if (keys.length > 0) {
        extractedMessage = `Log entry with: ${keys.join(', ')}`;
      } else {
        extractedMessage = `[${level.toUpperCase()}] Log entry`;
      }
    }
    
    actualMessage = extractedMessage;
    actualContext = message;
    actualError = context;
  }
  
  const entry = createLogEntry(level, actualMessage, actualContext, actualError);
  outputLog(entry);
}

/**
 * Logger interface
 */
interface LoggerInterface {
  debug: (message: string | LogContext, context?: LogContext) => void;
  info: (message: string | LogContext, context?: LogContext) => void;
  warn: (message: string | LogContext, context?: LogContext) => void;
  error: (message: string | LogContext, context?: LogContext, error?: unknown) => void;
  child: (sharedContext: LogContext) => LoggerInterface;
  version?: string;
}

const logger: LoggerInterface = {
  debug: (message: string | LogContext, context?: LogContext) => log('debug', message, context),
  info: (message: string | LogContext, context?: LogContext) => log('info', message, context),
  warn: (message: string | LogContext, context?: LogContext) => log('warn', message, context),
  error: (message: string | LogContext, context?: LogContext, error?: unknown) => log('error', message, context, error),
  
  /**
   * Create child logger with shared context
   */
  child: (sharedContext: LogContext) => ({
    debug: (message: string | LogContext, context?: LogContext) => 
      log('debug', message, { ...sharedContext, ...context }),
    info: (message: string | LogContext, context?: LogContext) => 
      log('info', message, { ...sharedContext, ...context }),
    warn: (message: string | LogContext, context?: LogContext) => 
      log('warn', message, { ...sharedContext, ...context }),
    error: (message: string | LogContext, context?: LogContext, error?: unknown) => 
      log('error', message, { ...sharedContext, ...context }, error),
    child: () => logger, // Return the same logger for child
    version: '2.0.0',
  }),
  version: '2.0.0',
};

// Default export for backward compatibility
export default logger;

/**
 * Test function to verify logger is working
 */
export function testSecureLogger() {
  const env = getEnvironment();
  const level = getLogLevel();
  
  logger.debug('Secure logger test configuration', {
    environment: env,
    logLevel: level,
    shouldLogDebug: shouldLog('debug'),
    shouldLogInfo: shouldLog('info'),
    shouldLogWarn: shouldLog('warn'),
    shouldLogError: shouldLog('error')
  });
  
  logger.debug('Test debug message', { test: true });
  logger.info('Test info message', { test: true });
  logger.warn('Test warn message', { test: true });
  logger.error('Test error message', { test: true });
} 

/**
 * Get stored logs for debugging (development only)
 */
export function getStoredLogs(): string[] {
  if (typeof window !== 'undefined') {
    return window.__secureLoggerLogs || [];
  }
  return [];
}

/**
 * Clear stored logs
 */
export function clearStoredLogs(): void {
  if (typeof window !== 'undefined') {
    window.__secureLoggerLogs = [];
  }
}

/**
 * Make logs available globally for debugging (development only)
 */
if (typeof window !== 'undefined' && getEnvironment() === 'development') {
  window.viewSecureLogs = () => {
    const logs = getStoredLogs();
    return logs;
  };
  
  window.clearSecureLogs = () => {
    clearStoredLogs();
  };
} 